import type { MarketplaceOrder } from "@/features/marketplace/types/marketplace.types";

import type {
  HubChromeOpportunityRow,
  HubChromeOrderRow,
  HubChromeScriptRow,
} from "./hub-chrome.types.js";
import type { OpportunityHubTabKey } from "./hub-chrome.types.js";

/** Levels a user can select on. Sent to vendor is terminal — nothing flows out of it. */
export type OpportunityHubSelectionLevel = "sets" | "opportunities" | "scripts";

const SELECTION_LEVELS: OpportunityHubSelectionLevel[] = ["sets", "opportunities", "scripts"];

export type OpportunityHubSelection = Record<OpportunityHubSelectionLevel, string[]>;

export const EMPTY_OPPORTUNITY_HUB_SELECTION: OpportunityHubSelection = {
  sets: [],
  opportunities: [],
  scripts: [],
};

/**
 * Each tab shows its own selection. An opportunity selection stays beside
 * Opportunities. A script selection stays on Scripts & Briefs. Production
 * does not repeat either chip.
 */
export interface OpportunityHubTabPill {
  level: OpportunityHubSelectionLevel;
  count: number;
  isOwnSelection: boolean;
}

export function scopeOpportunityRows(
  rows: HubChromeOpportunityRow[],
  selection: OpportunityHubSelection,
): HubChromeOpportunityRow[] {
  if (selection.sets.length === 0) return rows;
  const sets = new Set(selection.sets);
  return rows.filter((row) => sets.has(row.setId));
}

export function scopeScriptRows(
  rows: HubChromeScriptRow[],
  selection: OpportunityHubSelection,
): HubChromeScriptRow[] {
  if (selection.opportunities.length > 0) {
    const opportunities = new Set(selection.opportunities);
    return rows.filter((row) => opportunities.has(row.opportunityId));
  }
  if (selection.sets.length > 0) {
    const sets = new Set(selection.sets);
    return rows.filter((row) => sets.has(row.setId));
  }
  return rows;
}

export function scopeOrderRows(
  rows: HubChromeOrderRow[],
  selection: OpportunityHubSelection,
): HubChromeOrderRow[] {
  if (selection.scripts.length > 0) {
    const scripts = new Set(selection.scripts);
    return rows.filter((row) => scripts.has(row.scriptId));
  }
  if (selection.opportunities.length > 0) {
    const opportunities = new Set(selection.opportunities);
    return rows.filter((row) => opportunities.has(row.opportunityId));
  }
  if (selection.sets.length > 0) {
    const sets = new Set(selection.sets);
    return rows.filter((row) => sets.has(row.setId));
  }
  return rows;
}

function lineScriptIds(order: MarketplaceOrder): string[] {
  return order.scripts.flatMap((line) => {
    const ids: string[] = [];
    if (line.scriptId) ids.push(line.scriptId);
    if (line.briefId) ids.push(line.briefId);
    return ids;
  });
}

function lineOpportunityIds(order: MarketplaceOrder): string[] {
  return order.scripts.flatMap((line) => {
    const ids: string[] = [];
    const opportunity = line.script?.opportunity;
    if (opportunity?.id) ids.push(opportunity.id);
    if (line.script?.opportunityId != null) ids.push(String(line.script.opportunityId));
    return ids;
  });
}

function lineSetIds(order: MarketplaceOrder): string[] {
  return order.scripts.flatMap((line) => {
    const setId = line.script?.opportunity.setId;
    return setId ? [setId] : [];
  });
}

/**
 * Production orders follow the same cascade as flattened hub rows: scripts, then
 * opportunities, then sets. A matching line keeps the whole order.
 */
export function scopeMarketplaceOrders(
  orders: readonly MarketplaceOrder[],
  selection: OpportunityHubSelection,
): MarketplaceOrder[] {
  if (selection.scripts.length > 0) {
    const scripts = new Set(selection.scripts);
    return orders.filter((order) => lineScriptIds(order).some((id) => scripts.has(id)));
  }
  if (selection.opportunities.length > 0) {
    const opportunities = new Set(selection.opportunities);
    return orders.filter((order) =>
      lineOpportunityIds(order).some((id) => opportunities.has(id)),
    );
  }
  if (selection.sets.length > 0) {
    const sets = new Set(selection.sets);
    return orders.filter((order) => lineSetIds(order).some((id) => sets.has(id)));
  }
  return [...orders];
}

/**
 * Meta resets the levels below whatever the user just touched, so a refinement
 * never survives a change to the scope it was made in.
 */
function resetBelow(
  selection: OpportunityHubSelection,
  level: OpportunityHubSelectionLevel,
  ids: string[],
): OpportunityHubSelection {
  const next = { ...selection, [level]: ids };
  for (const candidate of SELECTION_LEVELS.slice(SELECTION_LEVELS.indexOf(level) + 1)) {
    next[candidate] = [];
  }
  return next;
}

export function toggleHubSelection(
  selection: OpportunityHubSelection,
  level: OpportunityHubSelectionLevel,
  id: string,
): OpportunityHubSelection {
  const current = selection[level];
  const next = current.includes(id)
    ? current.filter((value) => value !== id)
    : [...current, id];
  return resetBelow(selection, level, next);
}

/**
 * One update for a folded group or a header checkbox. Selecting adds every
 * missing member; when they are already all selected, it clears just those ids.
 * Sequential single toggles would each read the same selection and only the
 * last write would stick.
 */
export function toggleHubSelectionMembers(
  selection: OpportunityHubSelection,
  level: OpportunityHubSelectionLevel,
  ids: string[],
): OpportunityHubSelection {
  const current = selection[level];
  const selected = new Set(current);
  const members = new Set(ids);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const next = allSelected
    ? current.filter((id) => !members.has(id))
    : [...current, ...ids.filter((id) => !selected.has(id))];
  return resetBelow(selection, level, next);
}

export function clearHubSelectionLevel(
  selection: OpportunityHubSelection,
  level: OpportunityHubSelectionLevel,
): OpportunityHubSelection {
  return resetBelow(selection, level, []);
}

/** Card open: one set, and nothing selected under it. */
export function selectOnlyHubSet(setId: string): OpportunityHubSelection {
  return resetBelow(EMPTY_OPPORTUNITY_HUB_SELECTION, "sets", [setId]);
}

export function hubTabPill(
  tab: OpportunityHubTabKey,
  selection: OpportunityHubSelection,
): OpportunityHubTabPill | null {
  if (tab === "sets") {
    return selection.sets.length > 0
      ? { level: "sets", count: selection.sets.length, isOwnSelection: true }
      : null;
  }
  if (tab === "opportunities") {
    return selection.opportunities.length > 0
      ? { level: "opportunities", count: selection.opportunities.length, isOwnSelection: true }
      : null;
  }
  if (tab === "scripts") {
    return selection.scripts.length > 0
      ? { level: "scripts", count: selection.scripts.length, isOwnSelection: true }
      : null;
  }
  return null;
}
